import { DatabaseBlockSchema } from '@blocksuite/affine-model';
import {
  BlockNotionHtmlAdapterExtension,
  type BlockNotionHtmlAdapterMatcher,
  HastUtils,
  isNotionDatabaseTable,
  notionDatabaseTableKey,
  NotionImportError,
  readNotionDatabaseHosts,
} from '@blocksuite/affine-shared/adapters';

const PLACED_DATABASES_KEY = 'notion:placed-databases';

/**
 * Each Notion database table of a page becomes the host block of the database
 * the import planned for its table key. Tables with the same key take their
 * hosts in table order. Rows, columns and views live in the database storage,
 * which the import writes after the page.
 */
export const databaseBlockNotionHtmlAdapterMatcher: BlockNotionHtmlAdapterMatcher =
  {
    flavour: DatabaseBlockSchema.model.flavour,
    toMatch: o => HastUtils.isElement(o.node) && isNotionDatabaseTable(o.node),
    fromMatch: () => false,
    toBlockSnapshot: {
      enter: (o, context) => {
        if (!HastUtils.isElement(o.node)) return;
        const { walkerContext, configs } = context;
        const placed =
          (walkerContext.getGlobalContext(PLACED_DATABASES_KEY) as
            | string[]
            | undefined) ?? [];
        const container =
          o.parent && HastUtils.isElement(o.parent.node)
            ? o.parent.node
            : undefined;
        const tableKey = notionDatabaseTableKey(o.node, container);
        const host = readNotionDatabaseHosts(configs).find(
          candidate =>
            candidate.tableKey === tableKey &&
            !placed.includes(candidate.databaseId)
        );
        if (!host) {
          throw new NotionImportError(
            'table-without-database',
            tableKey,
            `Notion database table ${tableKey} of the page has no planned database.`
          );
        }
        walkerContext.setGlobalContext(PLACED_DATABASES_KEY, [
          ...placed,
          host.databaseId,
        ]);
        walkerContext
          .openNode(
            {
              type: 'block',
              id: host.databaseId,
              flavour: DatabaseBlockSchema.model.flavour,
              props: {
                databaseId: host.databaseId,
                databaseRootDocId: host.databaseRootDocId,
                databaseStorageVersion: host.databaseStorageVersion,
                title: {
                  '$blocksuite:internal:text$': true,
                  delta: host.title ? [{ insert: host.title }] : [],
                },
              },
              children: [],
            },
            'children'
          )
          .closeNode();
        walkerContext.skipAllChildren();
      },
    },
    fromBlockSnapshot: {},
  };

export const DatabaseBlockNotionHtmlAdapterExtension =
  BlockNotionHtmlAdapterExtension(databaseBlockNotionHtmlAdapterMatcher);
